import { type FC, type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';

import type { PostSortValue } from '~/constants/community';

import { BottomSheetDialog } from '../../ui/BottomSheetDialog';

import { usePostSortOrder } from '../../../_hooks/usePostSortOrder';
import type { SortTriggerComponentProps } from './sortTrigger.types';

const DEFAULT_TRIGGER_CLASS = 'flex min-w-0 items-center gap-2 appearance-none border-none bg-transparent p-0';

export const MobileSortSheet: FC<SortTriggerComponentProps> = ({ label, className, buttonProps, buttonContent }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { options, currentOption, setSort } = usePostSortOrder();

  const handleSelect = (value: PostSortValue) => {
    setSort(value);
    setIsOpen(false);
  };

  const ariaLabel = t('community.sort.aria_label', { name: label, current: currentOption.label });

  const triggerButton = (
    <button
      type="button"
      className={DEFAULT_TRIGGER_CLASS}
      {...buttonProps}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        buttonProps?.onClick?.(event);
        setIsOpen(true);
      }}
    >
      {buttonContent ?? (
        <>
          <span className="truncate text-xl font-semibold leading-6 tracking-[-0.4px]">{label}</span>
          <ChevronDownIcon className="size-4 shrink-0" aria-hidden="true" />
        </>
      )}
    </button>
  );

  return (
    <>
      {buttonProps ? (
        <span className={className}>{triggerButton}</span>
      ) : (
        <h1 className={cx('contents', className)}>{triggerButton}</h1>
      )}

      <BottomSheetDialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className="relative flex items-center justify-center px-5 pt-5 pb-2">
          <Dialog.Title className="text-base font-semibold text-black">{t('community.sort.sort_by')}</Dialog.Title>
          <button
            type="button"
            className="absolute right-5 flex items-center justify-center size-8 rounded-full hover:bg-black/5"
            onClick={() => setIsOpen(false)}
            aria-label={t('shared.close')}
          >
            <XMarkIcon className="size-5 text-black/50" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col pb-2">
          {options.map((option) => {
            const selected = currentOption.value === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                aria-current={selected ? 'true' : undefined}
                className={cx(
                  'flex items-center justify-between w-full h-16 px-5 text-lg text-black transition-colors hover:bg-black/5',
                  { 'font-medium': selected },
                )}
              >
                <span>{option.label}</span>
                {selected && <CheckIcon className="size-5 text-black/30" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </BottomSheetDialog>
    </>
  );
};
